/**
 * Lightweight fuzzy matcher for the command palette.
 *
 * Matches `query` as a case-insensitive subsequence of `text` and scores the
 * match so that prefix matches > substring matches > word-boundary
 * subsequences > scattered subsequences. Returns matched character indices so
 * the UI can highlight them.
 */

const WORD_BOUNDARY = /[\s\-_/.,:;'"()[\]{}]/;

const isBoundary = (text, index) => {
  if (index === 0) return true;
  const prev = text[index - 1];
  if (WORD_BOUNDARY.test(prev)) return true;
  // camelCase boundary: lowercase followed by uppercase
  return prev === prev.toLowerCase() && text[index] !== text[index].toLowerCase();
};

/**
 * Greedy subsequence match starting the first query character at or after
 * `startAt`, preferring word-boundary positions when scanning.
 */
const matchFrom = (query, text, lowerText, boundaryFirst) => {
  const indices = [];
  let textIndex = 0;

  for (let qi = 0; qi < query.length; qi += 1) {
    const qChar = query[qi];
    let found = -1;

    if (boundaryFirst) {
      // Prefer the next occurrence that sits on a word boundary.
      for (let ti = textIndex; ti < text.length; ti += 1) {
        if (lowerText[ti] === qChar && isBoundary(text, ti)) {
          found = ti;
          break;
        }
      }
    }
    if (found === -1) {
      found = lowerText.indexOf(qChar, textIndex);
    }
    if (found === -1) return null;

    indices.push(found);
    textIndex = found + 1;
  }

  return indices;
};

const scoreIndices = (indices, text) => {
  let score = 0;

  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i];
    score += 1;
    if (isBoundary(text, index)) score += 10;
    if (i > 0) {
      const gap = index - indices[i - 1] - 1;
      if (gap === 0) score += 8;
      else score -= Math.min(gap, 3);
    }
  }

  // Earlier first match reads as more relevant.
  score += Math.max(0, 10 - indices[0]);
  return score;
};

/**
 * @param {string} query
 * @param {string} text
 * @returns {{ score: number, indices: number[] } | null} null when no match
 */
export const fuzzyMatch = (query, text) => {
  if (typeof text !== 'string' || text.length === 0) return null;
  const trimmed = (query || '').trim().toLowerCase();
  if (!trimmed) return { score: 0, indices: [] };

  const lowerText = text.toLowerCase();

  // Exact substring gets a strong bonus; prefix even stronger.
  const substringAt = lowerText.indexOf(trimmed);
  if (substringAt !== -1) {
    const indices = Array.from({ length: trimmed.length }, (_, i) => substringAt + i);
    let bonus = 40;
    if (substringAt === 0) bonus += 20;
    else if (isBoundary(text, substringAt)) bonus += 10;
    return { score: scoreIndices(indices, text) + bonus, indices };
  }

  // Try boundary-preferring and plain greedy alignments; keep the best.
  const candidates = [
    matchFrom(trimmed, text, lowerText, true),
    matchFrom(trimmed, text, lowerText, false),
  ].filter(Boolean);

  if (candidates.length === 0) return null;

  let best = null;
  candidates.forEach((indices) => {
    const score = scoreIndices(indices, text);
    if (!best || score > best.score) best = { score, indices };
  });
  return best;
};

/**
 * Score an item against a query across several weighted fields.
 *
 * @param {string} query
 * @param {{ title: string, subtitle?: string, keywords?: string }} item
 * @returns {{ score: number, titleIndices: number[] } | null}
 */
export const scoreItem = (query, item) => {
  const titleMatch = fuzzyMatch(query, item.title || '');
  const subtitleMatch = item.subtitle ? fuzzyMatch(query, item.subtitle) : null;
  const keywordMatch = item.keywords ? fuzzyMatch(query, item.keywords) : null;

  if (!titleMatch && !subtitleMatch && !keywordMatch) return null;

  const score = Math.max(
    titleMatch ? titleMatch.score * 2 : -Infinity,
    subtitleMatch ? subtitleMatch.score : -Infinity,
    keywordMatch ? keywordMatch.score : -Infinity
  );

  return { score, titleIndices: titleMatch ? titleMatch.indices : [] };
};

export default fuzzyMatch;
